/**
 * Multiselect Control Component
 * Renders multi-select dropdown
 */

import React, { useEffect, useState } from 'react';
import { Box, Chip, Select, MenuItem, FormHelperText, FormControl, InputLabel } from '@mui/material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { MultiselectSettings } from '../../../../types/ControlTypes';
import { LabelWithInfo } from './LabelWithInfo';

interface SelectOption {
  label: string;
  value: any;
}

export const MultiselectControl: React.FC<ControlComponentProps> = ({
  name,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  placeholder,
  description,
  required,
  control,
}) => {
  const settings = control.settings as MultiselectSettings;
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(!!settings?.datasource);
  const selectedValues = Array.isArray(value) ? value : [];

  useEffect(() => {
    if (settings?.options && Array.isArray(settings.options)) {
      setOptions(settings.options);
      return;
    }

    if (settings?.datasource) {
      fetchDatasource();
    }
  }, [settings?.datasource, settings?.options]);

  const fetchDatasource = async () => {
    if (!settings?.datasource) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/globals/collection/${settings.datasource}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          limit: 100,
          searchProp: settings.renderProp || 'name'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const items = (data.data?.items || data.items || []).map((item: any) => {
          // Handle nested data structure: item.data.fieldName
          const itemData = item.data || item;
          return {
            label: itemData[settings.renderProp || 'name'] || String(itemData[settings.keyProp || 'id']),
            value: itemData[settings.keyProp || 'id'],
          };
        });
        setOptions(items);
      }
    } catch (err) {
      console.error('Failed to fetch datasource:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const newValues = typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : e.target.value;

    // Apply max selections limit if set
    let finalValues = newValues;
    if (settings?.maxSelections && newValues.length > settings.maxSelections) {
      finalValues = newValues.slice(0, settings.maxSelections);
    }

    onChange(finalValues);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        <LabelWithInfo
          label={label || name}
          description={description}
          required={required}
        />
      </Box>
      <FormControl fullWidth size="small" error={!!error} disabled={disabled || readonly || loading}>
        <Select
          multiple
          value={selectedValues}
          onChange={handleChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as any[]).map((val) => {
                const opt = options.find((o) => o.value === val);
                return <Chip key={val} label={opt?.label || val} size="small" />;
              })}
            </Box>
          )}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export default MultiselectControl;
